/**
 * Vehicle Demand Page - Redesigned
 * Shows Top 10 Most Viewed and Top 10 Most Unlocked vehicles
 * No demand labels or price bands - just real vehicle identity with actionable metrics
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Eye, Unlock, Car, Inbox, AlertCircle, RefreshCw } from "lucide-react";
import { TimeRangeSelector } from "../components/dashboard";
// Loading skeleton for vehicle list
const VehicleListSkeleton = () => (
  <div className="space-y-3">
    {Array.from({ length: 5 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 animate-pulse">
        <div className="w-7 h-7 rounded-full bg-gray-200" />
        <div className="flex-1">
          <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
          <div className="h-3 bg-gray-200 rounded w-1/2" />
        </div>
        <div className="w-16 h-6 bg-gray-200 rounded" />
      </div>
    ))}
  </div>
);

// Empty state component
const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mb-4">
      <Icon className="w-7 h-7 text-gray-400" />
    </div>
    <p className="text-base font-medium text-gray-500 mb-1">{title}</p>
    <p className="text-sm text-gray-400 max-w-xs">{subtitle}</p>
  </div>
);

// Mini horizontal bar showing views vs unlocks
const MiniBar = ({ views, unlocks }) => {
  const total = Math.max(views + unlocks, 1);
  const viewsPct = Math.min((views / total) * 100, 100);
  const unlocksPct = Math.min((unlocks / total) * 100, 100);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: '60px', maxWidth: '80px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ flex: 1, height: '5px', background: '#E0E7FF', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ width: `${viewsPct}%`, height: '100%', background: '#3B82F6', borderRadius: '3px' }} />
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <div style={{ flex: 1, height: '5px', background: '#D1FAE5', borderRadius: '3px', overflow: 'hidden' }}>
          <div style={{ width: `${unlocksPct}%`, height: '100%', background: '#10B981', borderRadius: '3px' }} />
        </div>
      </div>
    </div>
  );
};

// Individual vehicle row in ranked list
const VehicleRow = ({ rank, vehicle, primaryMetric, secondaryMetric, primaryLabel, secondaryLabel }) => {
  // Format vehicle title
  const title = [vehicle.year, vehicle.make, vehicle.model, vehicle.trim]
    .filter(Boolean)
    .join(" ");
  
  // Format display title - show VIN if no vehicle info
  const displayTitle = title || (vehicle.vin ? `VIN: ${vehicle.vin}` : "Unknown Vehicle");
  
  // Format stock/VIN identifier (only show if we have a title, otherwise VIN is in title)
  const identifier = title ? (
    vehicle.stock_number 
      ? `Stock: ${vehicle.stock_number}` 
      : vehicle.vin 
        ? `VIN: ...${vehicle.vin.slice(-6)}` 
        : vehicle.vin_last6 
          ? `VIN: ...${vehicle.vin_last6}` 
          : null
  ) : (
    vehicle.stock_number ? `Stock: ${vehicle.stock_number}` : null
  );

  return (
    <div className="flex items-center gap-3 py-3 border-b border-gray-100 last:border-0">
      {/* Rank Badge */}
      <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
        rank <= 3 
          ? 'bg-blue-500 text-white' 
          : 'bg-gray-100 text-gray-600'
      }`}>
        {rank}
      </div>
      
      {/* Vehicle Info */}
      <div className="flex-1 min-w-0">
        <p className={`font-medium truncate ${title ? 'text-gray-900' : 'text-gray-500 font-mono text-sm'}`}>
          {displayTitle}
        </p>
        {identifier && (
          <p className="text-xs text-gray-500 truncate">{identifier}</p>
        )}
      </div>
      {/* Mini bar chart: views (blue) vs unlocks (green) */}
      <MiniBar views={vehicle.views_count || 0} unlocks={vehicle.unlocks_count || 0} />
      
      {/* Metrics */}
      <div className="flex-shrink-0 text-right">
        <p className="font-bold text-gray-900 tabular-nums">{primaryMetric.toLocaleString()}</p>
        <p className="text-xs text-gray-400">{primaryLabel}</p>
      </div>
      
      {/* Secondary metric (smaller) */}
      <div className="flex-shrink-0 w-16 text-right">
        <p className="text-sm text-gray-500 tabular-nums">{secondaryMetric.toLocaleString()}</p>
        <p className="text-xs text-gray-400">{secondaryLabel}</p>
      </div>
    </div>
  );
};

const VehicleDemandPage = () => {
  const { apiFetch } = useAuth();
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topViewed, setTopViewed] = useState([]);
  const [topUnlocked, setTopUnlocked] = useState([]);

  // Fetch both lists in parallel
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [viewsRes, unlocksRes] = await Promise.all([
        apiFetch(`/analytics/top-vehicles?days=${days}&metric=views&limit=10`),
        apiFetch(`/analytics/top-vehicles?days=${days}&metric=unlocks&limit=10`)
      ]);
      
      setTopViewed(viewsRes.data.items || []);
      setTopUnlocked(unlocksRes.data.items || []);
    } catch (err) {
      console.error("Failed to fetch top vehicles:", err);
      setError(err.response?.data?.detail || "Failed to load vehicle data");
    } finally {
      setLoading(false);
    }
  }, [apiFetch, days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Error state
  if (error && !loading) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center min-h-[400px] bg-red-50 rounded-xl">
          <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
          <p className="text-lg font-medium text-red-600">Failed to load vehicle data</p>
          <p className="text-sm text-red-500 mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 flex items-center gap-2 px-4 py-2 bg-red-100 text-red-600 rounded-lg hover:bg-red-200 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 w-full" data-testid="vehicle-demand-page">
      {/* Header with Time Range Selector */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vehicle Demand</h1>
          <p className="text-gray-500 text-sm mt-0.5">Top performing vehicles by views and unlocks</p>
        </div>
        <TimeRangeSelector value={days} onChange={setDays} disabled={loading} />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top 10 Most Viewed */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-500" />
                <CardTitle className="text-lg">Top 10 Most Viewed</CardTitle>
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                Last {days} days
              </span>
            </div>
            <CardDescription>Vehicles with the most page views</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <VehicleListSkeleton />
            ) : topViewed.length === 0 ? (
              <EmptyState
                icon={Car}
                title="No vehicle views yet"
                subtitle="Once visitors view vehicles on your site, the top performers will appear here."
              />
            ) : (
              <div className="space-y-0">
                {topViewed.map((vehicle, idx) => (
                  <VehicleRow
                    key={`view-${vehicle.vin || idx}-${vehicle.year}-${vehicle.make}-${vehicle.model}`}
                    rank={idx + 1}
                    vehicle={vehicle}
                    primaryMetric={vehicle.views_count}
                    primaryLabel="views"
                    secondaryMetric={vehicle.unlocks_count}
                    secondaryLabel="unlocks"
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top 10 Most Unlocked */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Unlock className="w-5 h-5 text-amber-500" />
                <CardTitle className="text-lg">Top 10 Most Unlocked</CardTitle>
              </div>
              <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                Last {days} days
              </span>
            </div>
            <CardDescription>Vehicles with the most verified price unlocks</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <VehicleListSkeleton />
            ) : topUnlocked.length === 0 ? (
              <EmptyState
                icon={Unlock}
                title="No unlocks yet"
                subtitle="Once visitors unlock prices on your site, the top performers will appear here."
              />
            ) : (
              <div className="space-y-0">
                {topUnlocked.map((vehicle, idx) => (
                  <VehicleRow
                    key={`unlock-${vehicle.vin || idx}-${vehicle.year}-${vehicle.make}-${vehicle.model}`}
                    rank={idx + 1}
                    vehicle={vehicle}
                    primaryMetric={vehicle.unlocks_count}
                    primaryLabel="unlocks"
                    secondaryMetric={vehicle.views_count}
                    secondaryLabel="views"
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default VehicleDemandPage;
